import {AttrsSchema, Component} from 'panel';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DummyLocalStorageManager} from 'common/contexts/local-storage-manager/dummy';
import {LocalStorageKey} from 'common/report/constants';
import {getUserIdentityFromCookie} from 'common/report/util/user-identity-from-cookie';
import {emitTrackEvent} from 'common/util/dom';
import {getReportName} from 'common/report/util';

import {PersistedSidebarState, SidebarAttrs, SidebarJsxProps, SidebarState} from './types';
import {
  DEFAULT_PERSISTED_STATE,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_RESIZE_SNAP_WINDOW,
  TrackingEvent,
} from './constants';
import {template} from './template';
import css from './index.styl?inline';

const COMPONENT_NAME = `mp-sidebar`;

export class Sidebar extends Component<SidebarState, SidebarAttrs, any, any, IronContextRegistry> {
  private collapseMediaQuery: MediaQueryList;
  private collapseMediaQueryListener: (ev: MediaQueryListEvent) => void;
  private userLocalStorageHelper: UserLocalStorageHelper<PersistedSidebarState>;

  static get attrsSchema(): AttrsSchema<SidebarAttrs> {
    return {
      loading: {type: `boolean`},
      'min-adjacent-width': {
        type: `number`,
        default: 1110,
        description: `minimum width for the horizontally adjacent content on the page. used to block resizing and collapsing the sidebar when necessary.`,
      },
      'min-width': {
        type: `number`,
        default: SIDEBAR_MIN_WIDTH,
        description: `minimum width for the sidebar content`,
      },
      'default-width': {
        type: `number`,
        default: SIDEBAR_DEFAULT_WIDTH,
        description: `default width of the sidebar content`,
      },
      'storage-key': {type: `string`, default: `default`},
      'show-collapse-button': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`localStorageManager` as const],
      defaultContexts: {
        localStorageManager: new DummyLocalStorageManager(),
      },
      defaultState: {
        activeDragSectionIdx: null,
        canExpand: true,
        hoveredItemValues: new Set([]),
        isHovering: false,
        isOverflowMenuOpen: false,
        isResizing: false,
        isScrolling: false,
        sections: [],
        selectedItem: null,
        selectedSectionIndex: null,
        ...DEFAULT_PERSISTED_STATE,
      },
      hooks: {
        postUpdate: (_state: SidebarState) =>
          this.helpers.isExpandedStyle() ? this.classList.remove(`collapsed`) : this.classList.add(`collapsed`),
      },
      helpers: {
        startResizing: (ev: MouseEvent) => {
          if (ev.button !== 0) {
            return;
          }
          ev.preventDefault();

          this.update({isResizing: true});

          const {width} = this.state;
          const resizeHandler = (mouseMoveEvent: MouseEvent) => this.onSidebarResize(ev.clientX, width, mouseMoveEvent);

          const stopResizingHandler = () => {
            window.removeEventListener(`mousemove`, resizeHandler);
            window.removeEventListener(`mouseup`, stopResizingHandler);
            this.update({isResizing: false});
            this.setPersistedState();
            this.track(TrackingEvent.Resize, {width: this.state.width});
          };

          window.addEventListener(`mousemove`, resizeHandler);
          window.addEventListener(`mouseup`, stopResizingHandler);
        },

        isExpandedStyle: () => {
          return !this.state.isCollapsed || this.state.isHovering || this.state.isResizing;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.userLocalStorageHelper = new UserLocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.Sidebar,
    });

    this.update(this.getPersistedState());
    this.setupMediaQuery();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.collapseMediaQuery.removeEventListener) {
      this.collapseMediaQuery.removeEventListener(`change`, this.collapseMediaQueryListener);
    } else if (this.collapseMediaQuery.removeListener) {
      // Safari's mediaQuery still uses removeListener it seems
      this.collapseMediaQuery.removeListener(this.collapseMediaQueryListener);
    }
  }

  private getMaxAvailableWidth(): number {
    return document.documentElement.clientWidth - this.attr(`min-adjacent-width`);
  }

  private onSidebarResize(initialX: number, initialWidth: number, ev: MouseEvent) {
    ev.stopPropagation();
    ev.preventDefault();
    const delta = ev.clientX - initialX;
    const maxWidth = this.getMaxAvailableWidth();

    let width;
    if (!this.state.isCollapsed && initialWidth + delta > maxWidth) {
      // Cap the width if there's no more room
      width = Math.min(maxWidth, SIDEBAR_MAX_WIDTH);
    } else if (Math.abs(initialWidth + delta - this.attr(`default-width`)) < SIDEBAR_RESIZE_SNAP_WINDOW) {
      // "Snap" to the default width if it's within the window
      width = this.attr(`default-width`);
    } else {
      // Apply min/max bounds
      width = Math.min(Math.max(initialWidth + delta, this.attr(`min-width`)), SIDEBAR_MAX_WIDTH);
    }

    this.update({width});
    this.dispatchResizeEvent();
  }

  private dispatchResizeEvent() {
    const width = this.state.isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : this.state.width;
    this.dispatchEvent(new CustomEvent(`sidebarResize`, {detail: {width}}));
  }

  getPersistedState(): PersistedSidebarState {
    const persistedState = this.userLocalStorageHelper.getItem({
      key: this.attr(`storage-key`),
      userId: getUserIdentityFromCookie()?.id,
    });

    if (!persistedState) {
      return DEFAULT_PERSISTED_STATE;
    }

    return {
      isCollapsed: persistedState.isCollapsed,
      width: persistedState.width,
    };
  }

  private setPersistedState() {
    const {isCollapsed, width} = this.state;
    const persistedState = {isCollapsed, width};

    this.userLocalStorageHelper.setItem({
      userId: getUserIdentityFromCookie()?.id,
      key: this.attr(`storage-key`),
      value: persistedState,
    });
  }

  private setupMediaQuery() {
    if (this.collapseMediaQuery) {
      if (this.collapseMediaQuery.removeEventListener) {
        this.collapseMediaQuery.removeEventListener(`change`, this.collapseMediaQueryListener);
      } else if (this.collapseMediaQuery.removeListener) {
        // Safari's mediaQuery still uses removeListener it seems
        this.collapseMediaQuery.removeListener(this.collapseMediaQueryListener);
      }
    }

    this.collapseMediaQuery = window.matchMedia(
      `screen and (max-width: ${this.attr(`min-adjacent-width`) + this.attr(`min-width`)}px)`,
    );

    this.collapseMediaQueryListener = (ev: MediaQueryListEvent) => this.onMediaQueryMatch(ev.matches);
    this.onMediaQueryMatch(this.collapseMediaQuery.matches);

    if (this.collapseMediaQuery.addEventListener) {
      this.collapseMediaQuery.addEventListener(`change`, this.collapseMediaQueryListener);
    } else if (this.collapseMediaQuery.addListener) {
      // Safari's mediaQuery still uses addListener it seems
      this.collapseMediaQuery.addListener(this.collapseMediaQueryListener);
    }
  }

  private onMediaQueryMatch(matches: boolean) {
    const state = this.getPersistedState();
    if (matches) {
      this.update({
        isCollapsed: true,
        canExpand: false,
        width: state.width,
        isHovering: false,
      });
    } else {
      this.update({
        isCollapsed: state.isCollapsed,
        canExpand: true,
      });
    }
    this.dispatchResizeEvent();
  }

  toggleCollapsed() {
    if (this.state.canExpand) {
      const isCollapsed = !this.state.isCollapsed;
      this.update({isCollapsed});
      this.dispatchResizeEvent();
      this.setPersistedState();
      this.track(isCollapsed ? TrackingEvent.CollapsedSidebar : TrackingEvent.ExpandedSidebar);
    }
  }

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: getReportName(),
        ...props,
      },
    });
  }
}

export const SidebarEl = defineAndMakeJsxFactory<SidebarJsxProps>(COMPONENT_NAME, Sidebar);
